import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core';
import { ActivitySectionDirective, UserActivityClickTrackingDirective } from '@core';
import { HeaderComponent, PageHeaderBackSwipeConfig } from '@app/components/header/header.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { LanguageService, PageService } from '@app/services';
import { TextComponent } from '@app/components/text/text.component';
import { CallWaiterIndicatorComponent } from '@app/components/call-waiter-indicator/call-waiter-indicator.component';
import { TextTranslatePipe } from '@app/pipes/text-translate.pipe';
import { ImageTranslatePipe } from '@app/pipes/image-translate.pipe';
import { ScrollComponent } from '@app/components/scroll/scroll.component';
import { ColorTranslatePipe } from '@app/pipes/color-translate.pipe';
import { TextTranslatableSchema } from '@app/schemas/text';
import { PageViewMoreSchema } from '@app/views/page-more/page-more.schema';
import { ImageComponent } from '@app/components/image/image.component';
import { RefLinkParamsPipe } from '@app/pipes/ref-link-params.pipe';
import { HeaderBadgeTableNameComponent } from '@app/components/header/header-badge-table-name/header-badge-table-name.component';
import { HeaderTextComponent } from '@app/components/header/header-text/header-text.component';
import { HeaderBadgeTextComponent } from '@app/components/header/header-badge-text/header-badge-text.component';
import { pageHasServiceRequest } from '@app/builder/builder-page';

@Component({
  selector: 'app-page-more',
  templateUrl: 'page-more.component.html',
  styleUrl: 'page-more.component.scss',
  providers: [ColorTranslatePipe],
  imports: [
    ActivitySectionDirective,
    HeaderComponent,
    UserActivityClickTrackingDirective,
    RouterLink,
    TextComponent,
    HeaderTextComponent,
    CallWaiterIndicatorComponent,
    TextTranslatePipe,
    ImageTranslatePipe,
    ScrollComponent,
    ColorTranslatePipe,
    ImageComponent,
    RefLinkParamsPipe,
    HeaderBadgeTableNameComponent,
    HeaderTextComponent,
    HeaderBadgeTextComponent,
  ],
  host: {
    class: 'page',
    '[style]': 'styles()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageMoreComponent {

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly languageService = inject(LanguageService);
  private readonly colorTranslatePipe = inject(ColorTranslatePipe);
  private readonly pageService = inject(PageService);

  protected readonly backSwipeConfig: PageHeaderBackSwipeConfig = {
    direction: 'vertical',
    element: this.elementRef.nativeElement,
  };

  public readonly pageId = input.required<string>();
  public readonly pageName = input.required<TextTranslatableSchema>();
  public readonly pageViewConfig = input.required<PageViewMoreSchema>();

  protected readonly currentLanguage = toSignal(this.languageService.currentLanguage$, {
    requireSync: true,
  });

  protected readonly styles = computed(() => {
    const config = this.pageViewConfig();
    const language = this.currentLanguage();

    return {
      '--base-background-color': this.colorTranslatePipe.transform(config?.theme?.background?.color, language),
      '--base-foreground-color': this.colorTranslatePipe.transform(config?.theme?.foreground?.color, language),
    };
  });

  protected readonly pages = toSignal(this.pageService.config$);
  protected readonly pageIdsWithCallWaiter = computed(() => {
    const pages = this.pages() ?? [];

    return pages
      .filter(pageHasServiceRequest)
      .map((page) => page.id);
  });

  protected readonly availableItems = computed(() => {
    const pageConfig = this.pageViewConfig();
    const pages = this.pages() ?? [];

    return pageConfig.items.filter((button) => {
      if (button.type && button.action.type === 'page') {
        return this.pageService.findPageById(pages, button.action.pageId);
      }

      return true;
    });
  });

}
